// The consent cookie: its format, its parsing, and the tiny API anything that
// loads analytics or an ad tag must go through.
//
// ── Why this file is in marketing/ and the app imports it ─────────────────
//
// Both sites ask the same question and must read the same answer, so the
// format is written once. It cannot live under src/: the marketing bundle is
// forbidden src/lib, src/domain and the rest of the app tree
// (scripts/check-marketing-bundle.mjs). It can live here, because the app may
// import a file that imports nothing, and here it is *inside* that checker's
// source scan, so if this
// file ever grew an import of Firebase or React the build would say so. A
// neutral directory outside both would not be scanned at all. The app reaches
// it through src/lib/consent.js, which adds the brand's domain.
//
// So: **no imports in this file, ever.** Everything below is plain browser
// JavaScript.
//
// ── What it records, and what it decides ──────────────────────────────────
//
// One answer covers the app's Firebase Analytics (#458), Google Ads
// conversion measurement on both sites (#754) with ad personalisation still
// refused (`CONSENT_MODE` below), the Meta Pixel on both sites (#780,
// ./metaPixel.js; `CONSENT_MODE` is the Google tags' and is unchanged by it),
// and GA4 page views on the marketing site on the same Google tag (#783,
// ./adMeasurement.js). docs/operations.md §5a has the detail.
//
// **Opt-out (#755).** With no answer on record the measurement runs, and the
// banner is a notice with "OK" and "Decline"; a
// Decline, here or later from "Cookie settings", turns it off. A browser that
// sends Global Privacy Control (`navigator.globalPrivacyControl === true`)
// with no answer on record is treated as having declined, and is not shown
// the notice. An answer the visitor gave stands either way, so a GPC browser
// whose visitor chose to allow it in "Cookie settings" is allowed. Accepting
// the privacy policy at sign-up is notice, not consent, and nothing here
// reads it as either.
//
// So there are two readings of the cookie, and both are exported:
// `getConsentAnswer()` is what the visitor said ('granted', 'denied' or null),
// which decides whether the notice shows; `getConsent()` is what is in force
// ('granted' or 'denied', never null), which decides whether anything loads.
// src/lib/analytics.js, src/lib/adConversions.js, src/lib/metaPixel.js,
// ./adMeasurement.js and ./metaMeasurement.js are the only things that read
// `getConsent()` to decide anything. This cookie is
// the only one the banner itself adds, and it is strictly necessary — it is
// the record of the choice. Nothing here loads a tag.
//
// ── The cookie ────────────────────────────────────────────────────────────
//
//   coda_consent=v2.analytics=granted.1790000000
//                 │  │                │
//                 │  │                └ when the choice was made, unix seconds
//                 │  └ the one question asked, and the answer (the name is
//                 │    older than the question: see CONSENT_VERSION)
//                 └ format version — bump it to ask everyone again
//
// On the parent domain (`Domain=.<brand domain>`) so the marketing site and
// `app.<brand domain>` share one answer; host-only anywhere else (localhost, a
// preview channel on web.app — a public suffix, where a Domain attribute would
// make the browser drop the cookie outright). Path=/, SameSite=Lax, Secure
// over https. It lasts about twelve months and is then asked again: the age
// is checked from the timestamp in the value as well as by the cookie's own
// expiry, because Safari caps a script-set cookie at seven days and the value
// is the only clock that survives being re-set (see `renewConsent`).
//
// The name is the engine's, not the product's — the brand lives in
// design/brand.json alone (CLAUDE.md, "Brand").

export const CONSENT_COOKIE = 'coda_consent';
// Each version asked a wider question than the last: 1 while analytics "may"
// be added and no provider was named, 2 analytics alone, 3 with Google Ads
// conversion measurement (#754) and a banner saying "no ads aimed at you", 4
// with the Meta Pixel (#780), which may be used for exactly that. So a grant
// from an older version is not a yes to this one and reads as no answer
// (`parseConsent`): the notice shows again and, under opt-out, measurement
// runs until it is answered, unless the browser sends GPC.
//
// A decline from any version stays a decline (#755): under opt-out, no answer
// means measurement runs, so reading an old decline as no answer would switch
// on someone who said no. `renewConsent` re-sets it in the current format
// with its own date, so it still ends at twelve months. The opt-out switch
// (#755) did not bump the version: what a stored answer means did not change,
// only what *no* answer means.
//
// GA4 page views on the marketing site (#783) did not bump it either: version
// 4's banner already named Google Analytics, and counting the website's pages
// is the same purpose from the same provider.
//
// The value still says `analytics=`: the format is the version's to define,
// and renaming the key would change nothing but the parser.
export const CONSENT_VERSION = 4;
export const CONSENT_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;
// A timestamp further ahead than this is not clock skew, it is a bad value —
// and one that would otherwise never expire.
const FUTURE_SKEW_SECONDS = 24 * 60 * 60;

/**
 * What the one answer means to Google's Consent Mode v2, for every tag that
 * reads it — gtag.js under Firebase Analytics in the app, and the Google
 * Ads tag on both sites. A grant is analytics and conversion measurement:
 * `analytics_storage`, `ad_storage` (the `_gcl_*` cookies that remember an ad
 * click) and `ad_user_data` (sending the conversion to Google Ads). Never
 * `ad_personalization`: this is measurement, not remarketing, so no answer
 * to this banner can grant it (#754). A decline is all four denied.
 */
export const CONSENT_MODE = Object.freeze({
  granted: Object.freeze({
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'denied',
  }),
  denied: Object.freeze({
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  }),
});

const CHOICES = ['granted', 'denied'];
const VALUE = /^v(\d+)\.analytics=(granted|denied)\.(\d{1,12})$/;

/**
 * The value to store for `choice` made at `now` (unix seconds).
 * @param {'granted'|'denied'} choice
 * @param {number} now
 */
export function serializeConsent(choice, now) {
  if (!CHOICES.includes(choice)) throw new Error(`consent must be 'granted' or 'denied', not ${choice}`);
  return `v${CONSENT_VERSION}.analytics=${choice}.${Math.floor(now)}`;
}

/**
 * A stored value read back, or null when there is no current answer: absent,
 * malformed, an unknown format version, a grant from an older one, older than
 * twelve months, or dated in the future. Every one of those means "no answer"
 * — the notice shows, and measurement runs unless the browser sends GPC — and
 * none of them is an error. A decline from an older version is still a
 * decline (#755, `CONSENT_VERSION` says why).
 * @returns {{ analytics: 'granted'|'denied', at: number } | null}
 */
export function parseConsent(value, now) {
  const m = VALUE.exec(typeof value === 'string' ? value : '');
  if (!m) return null;
  const version = Number(m[1]);
  if (version < 1 || version > CONSENT_VERSION) return null;
  if (version < CONSENT_VERSION && m[2] !== 'denied') return null;
  const at = Number(m[3]);
  if (now - at >= CONSENT_MAX_AGE_SECONDS || at - now > FUTURE_SKEW_SECONDS) return null;
  return { analytics: m[2], at };
}

/**
 * The consent held in a `document.cookie` string. When more than one cookie
 * carries the name — a host-only one left from before the parent-domain one,
 * say — the most recent valid choice wins, which is the one the person made
 * last.
 */
export function readConsentCookie(cookieHeader, now) {
  let best = null;
  for (const part of String(cookieHeader || '').split(';')) {
    const eq = part.indexOf('=');
    if (eq === -1 || part.slice(0, eq).trim() !== CONSENT_COOKIE) continue;
    const parsed = parseConsent(part.slice(eq + 1).trim(), now);
    if (parsed && (!best || parsed.at >= best.at)) best = parsed;
  }
  return best;
}

/**
 * The Domain attribute for this host: `.<domain>` on the brand's own domain
 * or any subdomain of it, null (host-only) everywhere else.
 */
export function cookieDomainFor(hostname, domain) {
  if (!domain) return null;
  const host = String(hostname || '').toLowerCase().replace(/\.$/, '');
  const base = domain.toLowerCase();
  return host === base || host.endsWith(`.${base}`) ? `.${base}` : null;
}

/**
 * The whole `document.cookie` assignment for a stored value.
 * @param {string} value from serializeConsent
 * @param {{ hostname: string, protocol: string, domain?: string|null, maxAge?: number }} where
 */
export function consentCookieString(value, { hostname, protocol, domain, maxAge = CONSENT_MAX_AGE_SECONDS }) {
  const scope = cookieDomainFor(hostname, domain);
  return [
    `${CONSENT_COOKIE}=${value}`,
    `Max-Age=${Math.max(0, Math.floor(maxAge))}`,
    'Path=/',
    'SameSite=Lax',
    ...(scope ? [`Domain=${scope}`] : []),
    ...(protocol === 'https:' ? ['Secure'] : []),
  ].join('; ');
}

/**
 * What is in force, from the answer on record and the browser's privacy
 * signal: the answer when there is one, otherwise denied under Global
 * Privacy Control and granted without it (#755).
 * @param {'granted'|'denied'|null} answer
 * @param {boolean} privacySignal
 * @returns {'granted'|'denied'}
 */
export function effectiveConsent(answer, privacySignal) {
  if (answer === 'granted' || answer === 'denied') return answer;
  return privacySignal ? 'denied' : 'granted';
}

/** Whether a `navigator` sends Global Privacy Control. Only `true` counts. */
export function sendsPrivacySignal(nav) {
  try {
    return nav?.globalPrivacyControl === true;
  } catch {
    return false;
  }
}

// ── The browser half ──────────────────────────────────────────────────────
//
// The API an analytics or ad-tag loader is written against —
// docs/operations.md §5a says exactly how. `getConsent()` is 'granted' or
// 'denied', what is in force; `getConsentAnswer()` is 'granted', 'denied' or
// null (never asked, or asked too long ago); `needsConsentNotice()` is
// whether the banner shows by itself. `onConsentChange(fn)` hears every
// `setConsent` in this page, and the same change is dispatched on `window` as
// a `coda:consentchange` event for a script that does not import this module.
// Neither hears a choice made in another tab — a page reads the cookie when it
// loads, which is when a loader decides anything.

let parentDomain = null;
const listeners = new Set();

const nowSeconds = () => Math.floor(Date.now() / 1000);

/** Name the brand's domain, so the cookie is shared with its subdomains. */
export function configureConsent({ domain } = {}) {
  parentDomain = domain || null;
}

/** What the visitor answered. @returns {'granted'|'denied'|null} */
export function getConsentAnswer() {
  if (typeof document === 'undefined') return null;
  return readConsentCookie(document.cookie, nowSeconds())?.analytics ?? null;
}

/** Whether this browser sends Global Privacy Control. */
export function privacySignalOn() {
  return typeof navigator !== 'undefined' && sendsPrivacySignal(navigator);
}

/**
 * What is in force: 'granted' or 'denied'. Without a document nothing can
 * load, so it is 'denied' there.
 * @returns {'granted'|'denied'}
 */
export function getConsent() {
  if (typeof document === 'undefined') return 'denied';
  return effectiveConsent(getConsentAnswer(), privacySignalOn());
}

/**
 * Whether the notice shows by itself: no answer on record and no privacy
 * signal. A GPC browser has already said no, and the measurement the notice
 * is about is off for it, so there is nothing to tell it (#755); "Cookie
 * settings" still opens the banner and says the signal was honoured.
 */
export function needsConsentNotice() {
  return getConsentAnswer() === null && !privacySignalOn();
}

/** Record a choice, and tell every listener. */
export function setConsent(choice) {
  const value = serializeConsent(choice, nowSeconds());
  document.cookie = consentCookieString(value, { ...where(), domain: parentDomain });
  for (const fn of listeners) fn(choice);
  if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
    window.dispatchEvent(new CustomEvent('coda:consentchange', { detail: { analytics: choice } }));
  }
}

/** Subscribe to choices made in this page. Returns the unsubscribe. */
export function onConsentChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Re-set a current choice with the same value and whatever is left of its
 * twelve months. Called once when a page loads.
 *
 * This exists for Safari, which caps any cookie a script sets at seven days:
 * without it, every iPhone would be asked again each week. Re-setting on each
 * visit keeps the cookie alive while the value's own timestamp still ends it at
 * twelve months, so nothing here extends a choice beyond what was agreed. A
 * cookie set by the server in an HTTP response is not capped, and would make
 * this unnecessary; neither site has a server in front of its pages.
 */
export function renewConsent() {
  if (typeof document === 'undefined') return;
  const now = nowSeconds();
  const current = readConsentCookie(document.cookie, now);
  if (!current) return;
  document.cookie = consentCookieString(serializeConsent(current.analytics, current.at), {
    ...where(),
    domain: parentDomain,
    maxAge: CONSENT_MAX_AGE_SECONDS - (now - current.at),
  });
}

function where() {
  return { hostname: location.hostname, protocol: location.protocol };
}
